import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { it } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import {
  upsertPianificazioni,
  getPianificazioniForRange,
  normalizeTurnoRecord,
} from "../utils/pianificazioneDb";
import { addSecToDate } from "../utils";
import { api, ApiError } from "../api/client";
import { useMessage } from "../components/MessageProvider";

registerLocale("it", it);

const CACHE_WINDOW_DAYS = 30;

const toInputDate = (value = new Date()) => {
  if (typeof value === "string" && value.length >= 10) {
    return value.slice(0, 10);
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const shiftDate = (dateStr, deltaDays) => {
  const base = dateStr ? new Date(dateStr) : new Date();
  const next = addSecToDate(base, deltaDays * 24 * 60 * 60);
  return next ? toInputDate(next) : toInputDate(new Date());
};

const getTomorrowInputDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return toInputDate(tomorrow);
};

const getDateFromUrl = () => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const giornoParam = params.get("giorno") || params.get("data");
    if (!giornoParam) return null;
    const normalized = toInputDate(giornoParam);
    return normalized || null;
  } catch (err) {
    console.warn("Impossibile interpretare il parametro giorno dall'URL", err);
    return null;
  }
};

const ReadOnlyDateInput = React.forwardRef(
  ({ value, onClick, placeholder, className }, ref) => (
    <input
      readOnly
      ref={ref}
      value={value || ""}
      onClick={onClick}
      placeholder={placeholder}
      className={className}
    />
  )
);

const datePickerInputClass =
  "w-full rounded-2xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3 text-lg font-semibold text-gray-800 dark:text-gray-100 shadow-inner cursor-pointer text-center";

const formatItalianPickerValue = (dateStr) => {
  if (!dateStr || dateStr.length < 10) return "--/--/----";
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
};

const extractFascia = (oggetto = "") => {
  const parts = oggetto.split(",");
  return parts[1] ? parts[1].trim() : "";
};

const extractNomeTurno = (oggetto = "") => {
  if (typeof oggetto !== "string") return "";
  const parts = oggetto
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return "";
  return parts[parts.length - 1];
};

const parseTurnoDettagli = (turno) => {
  if (!turno) {
    return {
      nomeTurno: null,
      mansione: null,
      orarioTurno: null,
      orarioPausa: null,
    };
  }

  const nomeTurno = extractNomeTurno(turno.oggetto) || null;
  const rawTesto = typeof turno.testo === "string" ? turno.testo.trim() : "";
  let workingText = rawTesto;

  let orarioPausa = null;
  const pauseMatch = workingText.match(/(pause\s*:\s*.+)\s*$/i);
  if (pauseMatch) {
    orarioPausa = pauseMatch[1].trim() || null;
    workingText = workingText.slice(0, pauseMatch.index).trim();
  }

  let orarioTurno = null;
  const trailingOrarioMatch = workingText.match(/\(([^)]*\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}[^)]*)\)\s*$/);
  if (trailingOrarioMatch) {
    orarioTurno = trailingOrarioMatch[1].trim() || null;
    workingText = workingText.slice(0, trailingOrarioMatch.index).trim();
  } else {
    const looseOrarioMatch = rawTesto.match(/(\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}[A-Za-z]?)/);
    if (looseOrarioMatch) {
      orarioTurno = looseOrarioMatch[1].trim() || null;
    }
  }

  const mansione = workingText || null;

  return {
    nomeTurno,
    mansione,
    orarioTurno: orarioTurno || turno.fascia_oraria || null,
    orarioPausa,
  };
};

const buildQueryString = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.append(key, value);
  });
  return search.toString();
};

function Turni({ user, isOnline, onSetTopBarAction }) {
  const { showMessage } = useMessage();
  const [selectedDate, setSelectedDate] = useState(() => getDateFromUrl() || getTomorrowInputDate());
  const [turni, setTurni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [lastFetchedDate, setLastFetchedDate] = useState(null);
  const initialFetchDone = useRef(false);
  const selectedDateRef = useRef(selectedDate);
  const datePickerRef = useRef(null);

  useEffect(() => {
    if (!onSetTopBarAction) return;
    onSetTopBarAction({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
        </svg>
      ),
      label: "Info Turni",
      onClick: () => showMessage({
        title: "Turni",
        message: "Visualizza la pianificazione dei turni di lavoro. Puoi navigare tra le date per vedere i turni assegnati. I dati vengono aggiornati automaticamente quando sei online e restano disponibili anche offline grazie alla cache locale.",
        type: "info",
        timeout: 0,
      }),
    });
    return () => onSetTopBarAction(null);
  }, [onSetTopBarAction, showMessage]);

  useEffect(() => {
    initialFetchDone.current = false;
    setLastFetchedDate(null);
  }, [user?.id]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const loadFromCache = useCallback(
    async (targetDate) => {
      if (!user?.id || !targetDate) return;
      try {
        const cached = await getPianificazioniForRange(user.id, {
          dal: targetDate,
          al: targetDate,
        });
        setTurni(cached);
      } catch (err) {
        console.error("Errore lettura cache turni", err);
      }
    },
    [user?.id]
  );

  const requestTurni = useCallback(
    async ({ targetDate, silent = false } = {}) => {
      const fetchDate = targetDate || selectedDateRef.current;
      if (!user?.id || !fetchDate) return;
      setRefreshing(true);
      if (!silent) {
        setLoading(true);
      }
      try {
        let payload;
        try {
          payload = await api.post('/api/pianificazione', { session_token: user.token, dal: fetchDate, al: fetchDate, limit: 1 });
        } catch (errPost) {
          if (errPost instanceof ApiError) {
            throw new Error(`WS pianificazione ha risposto ${errPost.status}`);
          }
          throw errPost;
        }
        const rawList = Array.isArray(payload?.pianificazioni) ? payload.pianificazioni : [];
        const enriched = rawList.map((item) =>
              normalizeTurnoRecord(user.id, {
                ...item,
                data_riferimento: item.data_riferimento,
                fascia_oraria: extractFascia(item.oggetto),
                fetched_at: new Date().toISOString(),
              })
            );
        setTurni(enriched);
        await upsertPianificazioni(user.id, enriched);
        const timestamp = new Date();
        setLastSync({ timestamp, date: fetchDate });
        setLastFetchedDate(fetchDate);
        setError(null);
      } catch (err) {
        console.error("Errore fetch turni", err);
        setError(err.message || "Errore durante il recupero dei turni");
        await loadFromCache(fetchDate);
      } finally {
        setRefreshing(false);
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [user?.id, loadFromCache]
  );

  useEffect(() => {
    if (!user?.id || initialFetchDone.current) return;
    initialFetchDone.current = true;
    requestTurni({ targetDate: selectedDateRef.current });
  }, [user?.id, requestTurni]);

  useEffect(() => {
    if (!isOnline || !lastFetchedDate) return undefined;
    const interval = setInterval(() => {
      requestTurni({ targetDate: lastFetchedDate, silent: true });
    }, 60000);
    return () => clearInterval(interval);
  }, [isOnline, lastFetchedDate, requestTurni]);

  useEffect(() => {
    if (!turni.length) return;
    const hasSelection = turni.some((t) => t.data_riferimento === selectedDate);
    if (!hasSelection && turni[0]?.data_riferimento) {
      setSelectedDate((prev) => prev || turni[0].data_riferimento);
    }
  }, [turni, selectedDate]);

  const selectedTurno = useMemo(
    () => turni.find((t) => t.data_riferimento === selectedDate) || null,
    [turni, selectedDate]
  );

  const turnoDettagli = useMemo(() => parseTurnoDettagli(selectedTurno), [selectedTurno]);

  const statusMessage = useMemo(() => {
    if (!isOnline) return "Offline: visualizzazione dati salvati";
    if (refreshing) return "Aggiornamento in corso";
    if (lastSync?.timestamp) {
      const dateLabel = lastSync.timestamp.toLocaleDateString("it-IT");
      const timeLabel = lastSync.timestamp.toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `Aggiornato il ${dateLabel} alle ${timeLabel}`;
    }
    return "Ultimo aggiornamento non disponibile";
  }, [isOnline, refreshing, lastSync]);

  const setDate = useCallback(
    (nextDate, { fetchData = false } = {}) => {
      setSelectedDate(nextDate);
      if (fetchData) {
        requestTurni({ targetDate: nextDate });
      }
    },
    [requestTurni]
  );

  const moveSelectedDay = (delta) => {
    setDate(shiftDate(selectedDate || toInputDate(new Date()), delta), {
      fetchData: true,
    });
  };

  /*
   * Legacy manual refresh via search icon.
   * const handleSearch = () => {
   *   setDate(selectedDate, { fetchData: true });
   * };
   */

  const focusDatePicker = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen?.(true);
      datePickerRef.current.setFocus?.();
    }
  };

  const selectedDateObj = useMemo(() => {
    if (!selectedDate) return null;
    const [year, month, day] = selectedDate.split("-").map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [selectedDate]);

  return (
    <div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
    <div className="max-w-3xl mx-auto w-full px-4 py-8 space-y-6">
      <div className="rounded-3xl border-2 border-blu-uss/70 bg-white dark:bg-slate-900 p-5 shadow-lg">
        <div className="flex items-center gap-2 text-blu-uss">
          <svg
            viewBox="0 0 24 24"
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3.5" y="4.5" width="17" height="16" rx="2" />
            <path d="M8 2.5v4" />
            <path d="M16 2.5v4" />
            <path d="M3.5 9.5h17" />
          </svg>
          <p className="text-xl font-black uppercase tracking-wide">Giorno:</p>
        </div>
        <div className="mt-3 flex items-center justify-center gap-3">
          <div className="flex items-center justify-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => moveSelectedDay(-1)}
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border-2 border-blu-uss text-blu-uss text-2xl font-semibold hover:bg-blue-50 dark:border-blue-200 dark:text-blue-200"
              aria-label="Giorno precedente"
            >
              ◀
            </button>
            <div className="w-[150px] sm:w-[200px]">
              <DatePicker
                ref={datePickerRef}
                selected={selectedDateObj}
                onChange={(date) =>
                  setDate(date ? toInputDate(date) : selectedDate, { fetchData: true })
                }
                locale="it"
                dateFormat="dd/MM/yyyy"
                placeholderText="Seleziona il giorno"
                customInput={
                  <ReadOnlyDateInput
                    className={datePickerInputClass}
                    value={formatItalianPickerValue(selectedDate)}
                  />
                }
              />
            </div>
            <button
              type="button"
              onClick={() => moveSelectedDay(1)}
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border-2 border-blu-uss text-blu-uss text-2xl font-semibold hover:bg-blue-50 dark:border-blue-200 dark:text-blue-200"
              aria-label="Giorno successivo"
            >
              ▶
            </button>
          </div>
          {/**
           * Pulsante ricerca nascosto per ragioni di spazio su display piccoli.
           * Lasciato commentato per poter ripristinare rapidamente il comportamento precedente
           * (apertura del DatePicker via focusDatePicker) se richiesto.
           */}
          {false && (
            <>
              <div className="flex-1" aria-hidden="true" />
              <div className="flex-shrink-0 ml-3">
                <button
                  type="button"
                  onClick={focusDatePicker}
                  className={`flex h-12 w-12 items-center justify-center rounded-2xl border-2 text-2xl transition ${
                    refreshing
                      ? "border-amber-500 text-amber-500 animate-spin"
                      : isOnline
                      ? "border-blu-uss text-blu-uss"
                      : "border-gray-300 text-gray-300"
                  }`}
                  title={isOnline ? "Apri il selettore data" : "Disponibile solo online"}
                >
                  {refreshing ? "↻" : "🔍"}
                </button>
              </div>
            </>
          )}
        </div>
        <p className="mt-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
          {statusMessage}
        </p>
        {!isOnline && (
          <div className="mt-3 text-sm font-semibold text-amber-600 text-center">
            Sei offline: stai consultando i turni memorizzati sul dispositivo.
          </div>
        )}
      </div>

      <div className="rounded-3xl border-2 border-blu-uss bg-white dark:bg-slate-900 p-6 shadow">
        <div className="mb-2 flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <svg
            viewBox="0 0 24 24"
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="8" />
            <path d="M12 7v5l3 3" />
          </svg>
          <p className="text-sm font-semibold uppercase">Turno pianificato:</p>
        </div>
        {selectedTurno ? (
          <div className="space-y-2 text-center">
            <h2 className="text-xl font-bold text-blu-uss">
              {turnoDettagli.nomeTurno || "Turno non specificato"}
            </h2>
            <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
              {turnoDettagli.mansione || "Mansione non disponibile"}
            </p>
            <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
              {turnoDettagli.orarioTurno || "Orario non disponibile"}
            </p>
            <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
              {turnoDettagli.orarioPausa || "Pause non disponibili"}
            </p>
            <p className="mt-4 text-xs font-semibold text-gray-500 dark:text-gray-400">
              Data: {formatItalianPickerValue(selectedTurno.data_riferimento) || "--/--/----"}
            </p>
          </div>
        ) : (
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 text-center">
            Nessuna pianificazione per questa data.
          </p>
        )}
      </div>
      {loading && (
        <div className="p-4 rounded-2xl border border-dashed border-gray-300 text-center text-sm font-semibold text-gray-500">
          Caricamento turni in corso...
        </div>
      )}
      {error && (
        <div className="p-4 rounded-2xl border border-red-200 bg-red-50 text-sm font-semibold text-red-600">
          {error}
        </div>
      )}
      <p className="text-xs font-semibold text-gray-400 text-center">
        I dati vengono salvati in locale per almeno {CACHE_WINDOW_DAYS} giorni.
      </p>
    </div>
    </div>
  );
}

export default Turni;
